import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Alert
} from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import axios from '../../../api/axios';

const PauseListDialog = ({ open, onClose, userId, userName }) => {
    const [pauseList, setPauseList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchPauseList = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post('user/pause/list', {
                user_id: userId
            });

            if (response.data && response.data.pause_list) {
                setPauseList(response.data.pause_list);
            } else {
                setPauseList([]);
            }
        } catch (err) {
            console.error('Error fetching pause list:', err);
            setError('Failed to fetch pause list. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // Fetch pause list when dialog opens
    useEffect(() => {
        if (open && userId) {
            fetchPauseList();
        }
    }, [open, userId, fetchPauseList]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '80vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PauseIcon />
                    <Typography variant="h6">
                        Pause List - {userName}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            Loading pause list...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : pauseList.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <PauseIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No Pause History
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This user has no paused dates on record.
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                            <Typography variant="body2" color="text.secondary">
                                Total paused dates: <strong>{pauseList.length}</strong>
                            </Typography>
                        </Box>

                        <List dense>
                            {pauseList.sort((a, b) => new Date(b) - new Date(a)).map((date, index) => (
                                <ListItem key={date}>
                                    <ListItemText
                                        primary={date}
                                        primaryTypographyProps={{
                                            fontFamily: 'monospace'
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
                {pauseList.length > 0 && (
                    <Button
                        onClick={fetchPauseList}
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        Refresh
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PauseListDialog;
